import { useEffect, useMemo, useRef, useState } from "react";
import { useNesGlitch } from "./NesGlitchContext";
import {
    NES_SCREEN_HEIGHT,
    NES_SCREEN_WIDTH,
} from "../../utils/nesPpu";
import {
    buildSvgDocument,
    renderPixels,
} from "../../utils/pixelSvgRender";
import "./NesGlitch.scss";

const DISPLAY_WIDTH = 512;
const DISPLAY_HEIGHT = Math.round(
    (DISPLAY_WIDTH * NES_SCREEN_HEIGHT) / NES_SCREEN_WIDTH,
);
const RAW_OUTPUT_AUTO_LIMIT = 60_000;
const PREVIEW_ZOOM_MIN = 0.25;
const PREVIEW_ZOOM_MAX = 8;

export function NesGlitchViewer() {
    const {
        rgbaFrame,
        showOutputPane,
        outputMode,
        outputWidth,
        downloadSvg,
    } = useNesGlitch();

    const [previewZoom, setPreviewZoom] = useState(1);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const zoomIn = () => {
        setPreviewZoom((z) =>
            Math.min(
                PREVIEW_ZOOM_MAX,
                Math.round(z * 1.25 * 100) / 100,
            ),
        );
    };
    const zoomOut = () => {
        setPreviewZoom((z) =>
            Math.max(
                PREVIEW_ZOOM_MIN,
                Math.round((z / 1.25) * 100) / 100,
            ),
        );
    };
    const resetPreviewZoom = () => setPreviewZoom(1);

    const stageW = DISPLAY_WIDTH * previewZoom;
    const stageH = DISPLAY_HEIGHT * previewZoom;

    useEffect(() => {
        const cv = canvasRef.current;
        if (!cv) return;
        if (cv.width !== NES_SCREEN_WIDTH || cv.height !== NES_SCREEN_HEIGHT) {
            cv.width = NES_SCREEN_WIDTH;
            cv.height = NES_SCREEN_HEIGHT;
        }
        const ctx = cv.getContext("2d");
        if (!ctx) return;
        const img = ctx.createImageData(NES_SCREEN_WIDTH, NES_SCREEN_HEIGHT);
        const data = img.data;
        for (let i = 0, j = 0; i < rgbaFrame.length; i++, j += 4) {
            const v = rgbaFrame[i];
            data[j] = (v >>> 16) & 0xff;
            data[j + 1] = (v >>> 8) & 0xff;
            data[j + 2] = v & 0xff;
            data[j + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
    }, [rgbaFrame]);

    const outputResult = useMemo(() => {
        if (!showOutputPane) return null;
        const outH = Math.round(
            (outputWidth * NES_SCREEN_HEIGHT) / NES_SCREEN_WIDTH,
        );
        // Raw mode at full size emits one rect per pixel ⇒ 61,440 rects.
        // Allow it but warn once it gets very large.
        if (outputMode === "raw") {
            const cells = NES_SCREEN_WIDTH * NES_SCREEN_HEIGHT;
            if (cells > RAW_OUTPUT_AUTO_LIMIT) {
                return null;
            }
        }
        return renderPixels(
            rgbaFrame,
            NES_SCREEN_WIDTH,
            NES_SCREEN_HEIGHT,
            outputMode,
            outputWidth,
            outH,
        );
    }, [showOutputPane, outputMode, outputWidth, rgbaFrame]);

    const outputSvgString = useMemo(() => {
        if (!outputResult) return null;
        const outH = Math.round(
            (outputWidth * NES_SCREEN_HEIGHT) / NES_SCREEN_WIDTH,
        );
        return buildSvgDocument(outputResult.body, outputWidth, outH);
    }, [outputResult, outputWidth]);

    const outputSizeBytes = useMemo(() => {
        if (!outputSvgString) return 0;
        return new Blob([outputSvgString]).size;
    }, [outputSvgString]);

    return (
        <div className="panel-viewer nes-glitch-viewer">
            <div className="svg-zoom-controls">
                <button
                    type="button"
                    className="zoom-button"
                    onClick={zoomOut}
                    title="Zoom out"
                >
                    −
                </button>
                <span className="zoom-level">
                    {Math.round(previewZoom * 100)}%
                </span>
                <button
                    type="button"
                    className="zoom-button"
                    onClick={zoomIn}
                    title="Zoom in"
                >
                    +
                </button>
                <button
                    type="button"
                    className="zoom-button"
                    onClick={resetPreviewZoom}
                    title="Reset zoom"
                >
                    ⌂
                </button>
            </div>
            <div className="svg-scroll-wrapper nes-glitch-viewer-scroll">
                <div
                    className={[
                        "nes-glitch-panes",
                        !showOutputPane ? "nes-glitch-panes--single" : "",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                <div className="nes-glitch-pane">
                    <div className="nes-glitch-pane-header">
                        <h3>
                            Live frame ({NES_SCREEN_WIDTH}x{NES_SCREEN_HEIGHT})
                        </h3>
                    </div>
                    <div
                        className="nes-glitch-stage"
                        style={{ width: stageW, height: stageH }}
                    >
                        <canvas
                            ref={canvasRef}
                            className="nes-glitch__canvas"
                            width={NES_SCREEN_WIDTH}
                            height={NES_SCREEN_HEIGHT}
                            style={{
                                width: stageW,
                                height: stageH,
                            }}
                        />
                    </div>
                    <p className="nes-glitch-hint">
                        Composited bg + sprites with current scroll, attributes,
                        palette, and glitch pipeline.
                    </p>
                </div>

                {showOutputPane && (
                    <div className="nes-glitch-pane">
                        <div className="nes-glitch-pane-header">
                            <h3>
                                Output SVG ({outputModeLabel(outputMode)}){" "}
                                {outputWidth}x
                                {Math.round(
                                    (outputWidth * NES_SCREEN_HEIGHT) /
                                        NES_SCREEN_WIDTH,
                                )}
                            </h3>
                            <button
                                type="button"
                                className="generic-button"
                                onClick={downloadSvg}
                                title="Download the rendered SVG"
                            >
                                Download SVG
                            </button>
                        </div>
                        <div
                            className="nes-glitch-stage nes-glitch-stage--output"
                            style={{
                                width: stageW,
                                height: stageH,
                            }}
                        >
                            {outputSvgString ? (
                                <div
                                    className="nes-glitch__output"
                                    style={{
                                        width: stageW,
                                        height: stageH,
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: outputSvgString,
                                    }}
                                />
                            ) : (
                                <div className="nes-glitch-output-placeholder">
                                    <p>
                                        Raw mode at full screen emits ~61k
                                        rects. Switch to{" "}
                                        <em>Greedy rects</em> or{" "}
                                        <em>Per-color path</em> for a usable
                                        preview / file size.
                                    </p>
                                </div>
                            )}
                        </div>
                        {outputResult && (
                            <p className="nes-glitch-hint">
                                {outputResult.count.toLocaleString()}{" "}
                                {outputMode === "paths" ? "path" : "rect"}
                                {outputResult.count === 1 ? "" : "s"} -{" "}
                                {formatBytes(outputSizeBytes)}
                            </p>
                        )}
                    </div>
                )}
                </div>
            </div>
        </div>
    );
}

function outputModeLabel(mode: string): string {
    switch (mode) {
        case "raw":
            return "raw rects";
        case "greedy-rects":
            return "greedy rects";
        case "paths":
            return "per-color path";
        default:
            return mode;
    }
}

function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
